import {BalanceAdjustmentsWithMetaModel, BalanceAdjustmentsFilter} from '../model';
import {InfiniteData, useInfiniteQuery, UseInfiniteQueryResult} from '@tanstack/react-query';
import {projectApi} from '../api';
import {useCallback, useMemo, useState} from 'react';
export type BalanceAdjustmentsState = {
  getInfiniteBalanceAdjustments: UseInfiniteQueryResult<InfiniteData<BalanceAdjustmentsWithMetaModel>, Error>;
  filters: BalanceAdjustmentsFilter;
  updateFilter: (newFilter: Partial<BalanceAdjustmentsFilter>) => void;
};

const DEFAULT_PAGE_SIZE = 25;

export const useBalanceAdjustments = (initialFilter: BalanceAdjustmentsFilter = {}): BalanceAdjustmentsState => {
  const [filters, setFilters] = useState<BalanceAdjustmentsFilter>(() => ({
    pageSize: DEFAULT_PAGE_SIZE,
    ...initialFilter,
  }));

  const infiniteQueryFilters = useMemo(() => {
    const {after, ...rest} = filters as any;
    return rest;
  }, [filters]);

  const getInfiniteBalanceAdjustments = useInfiniteQuery<
    BalanceAdjustmentsWithMetaModel,
    Error,
    InfiniteData<BalanceAdjustmentsWithMetaModel>,
    ['balance-adjustments', 'infinite', Omit<BalanceAdjustmentsFilter, 'after'>],
    string | undefined
  >({
    queryKey: ['balance-adjustments', 'infinite', infiniteQueryFilters],
    queryFn: ({pageParam}) => {
      return projectApi.getBalanceAdjustments({
        ...infiniteQueryFilters,
        pageSize: filters.pageSize,
        after: pageParam,
      });
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage, _allPages, _lastPageParam) => {
      return lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined;
    },
    staleTime: 60 * 1000,
  });

  const updateFilter = useCallback((newFilter: Partial<BalanceAdjustmentsFilter>) => {
    setFilters((prev) => {
      return {
        ...prev,
        ...newFilter,
      };
    });
  }, []);

  return {
    getInfiniteBalanceAdjustments,
    filters,
    updateFilter,
  };
};
